import { Mail as MailIcon, Plus as PlusIcon } from 'iconoir-react'
import { Fragment, ReactElement } from 'react'
import { AddConversationModal } from 'app/components/inbox/AddConversationModal'
import { InboxSkeleton } from 'app/components/inbox/InboxSkeleton'
import { PageContent } from 'app/components/inbox/PageContent'
import { EmptySearch } from 'app/components/shared/styled/EmptySearch'
import Spinner from 'app/components/Spinner'
import type { LocationJson } from 'app/types/entities'
import { useMinimumLoading } from 'app/hooks/useMinimumLoading'
import { showModal } from 'app/lib/modal'
import { predictSmsThreadId } from 'app/lib/smsThreadId'
import type { UseInboxPageReturn } from './useInboxPage'

export type InboxPageComponentProps = UseInboxPageReturn

// Filters that come up empty while the location still holds conversations, where the
// default copy would read as a fault rather than a filter.
const EMPTY_STATE_BY_FILTER: Record<string, { title: string; description: string }> = {
  unread: {
    title: `No unread messages`,
    description: `You have no unread conversations for the selected location.`,
  },
  replied: {
    title: `No replies`,
    description: `No one has texted this location back yet.`,
  },
}

export function InboxPageComponent({
  data,
  handleSearchParams,
  controller,
  location,
  inboxLocations,
  isMobile,
  subnav,
}: InboxPageComponentProps): ReactElement {
  const {
    threadId,
    contactId,
    selectedContact,
    conversations,
    unreadLocations,
    displayedMessages,
    selectedPhone,
    addConversation,
    selectConversation,
    resetInboxState,
    clearSelection,
    activeConversation,
    archiveConversation,
    markThreadRead,
    markThreadUnread,
    handleMessageSent,
    setConversations,
    isLoading,
    hasSynced,
  } = controller
  const hasSmsAgentProduct = !!location?.products?.includes(`SMS Agent`)

  // Keep the loading UI visible for a minimum window so a warm revisit — where
  // the network-only sync resolves almost instantly from cache — shows the same
  // smooth transition as a cold load instead of blinking the skeleton/spinner.
  // Archive from inside the open thread. Undefined when nothing is selected, so
  // the header simply hides the control rather than rendering a dead button.
  const threadArchived = !!activeConversation?.archived
  const handleArchiveThread = activeConversation
    ? () => archiveConversation(activeConversation, !activeConversation.archived)
    : undefined

  const showListLoading = useMinimumLoading(!hasSynced)

  // A conversation started from the "Start a new conversation" modal has no
  // server thread id yet — the controller keys it by contact id, or by the
  // pending contact alone when the user typed a raw phone number. Gating the
  // conversation surface on `threadId` made "Start conversation" look inert:
  // the row appeared in the list but the pane stayed on the empty state.
  const hasActiveConversation = Boolean(threadId || contactId || selectedContact)
  // PageContent remounts per conversation; a pending one falls back to the phone
  // so switching between two new numbers still replays its load transition.
  const conversationKey = contactId ?? threadId ?? selectedContact?.cellPhone ?? ``

  // Mobile master-detail: once a location is chosen, land on the conversation
  // LIST (the subnav content rendered full-width in `main`). Tapping a row
  // selects a thread and swaps to the thread view with a Back control. The
  // location picker (locationId === '') falls through to the shared branch
  // below, which is already mobile-aware.
  if (isMobile && data.locationId !== ``) {
    if (hasActiveConversation) {
      return (
        <PageContent
          conversationKey={conversationKey}
          threadId={threadId}
          contact={selectedContact}
          messages={displayedMessages}
          messagesLoading={displayedMessages === undefined}
          selectedPhone={selectedPhone}
          hasSmsAgentProduct={hasSmsAgentProduct}
          onBack={() => {
            // Selecting a conversation persists it as `?tab=<threadId>`, which
            // resolveInboxSelection re-reads to re-select — so returning to the
            // list must drop that param too, not just the controller state.
            handleSearchParams({ tab: undefined })
            clearSelection()
          }}
          onMarkThreadRead={markThreadRead}
          onMarkThreadUnread={markThreadUnread}
          onArchiveThread={handleArchiveThread}
          threadArchived={threadArchived}
          onMessage={handleMessageSent}
        />
      )
    }
    const listChildren = subnav[0]?.children ?? []
    return listChildren.length > 0 ? (
      <div className="flex h-full flex-col bg-surface">
        {listChildren.map((child, i) => (
          <Fragment key={i}>
            {child.filter}
            {child.customComponent}
          </Fragment>
        ))}
      </div>
    ) : (
      <InboxSkeleton />
    )
  }

  return data.locationId === `` ? (
    // No chooser: the hook resolves a location and rewrites the URL, so this is
    // the brief window before that lands. A user entitled to the Inbox nav item
    // always has at least one inbox location, so the empty case is a workspace
    // mid-setup rather than a state we route people into.
    inboxLocations?.length === 0 ? (
      <div className="flex h-full w-full flex-col items-center justify-center">
        <EmptySearch
          icon={() => <MailIcon width={60} height={60} className="text-default" />}
          title="No inboxes available"
          description={`None of your locations have texting turned on.\nAsk your administrator to enable it.`}
        />
      </div>
    ) : (
      <div className="flex h-full w-full flex-col items-center justify-center">
        <Spinner size={20} />
      </div>
    )
  ) : hasActiveConversation ? (
    <PageContent
      conversationKey={conversationKey}
      threadId={threadId}
      contact={selectedContact}
      messages={displayedMessages}
      messagesLoading={displayedMessages === undefined}
      selectedPhone={selectedPhone}
      hasSmsAgentProduct={hasSmsAgentProduct}
      onMarkThreadRead={markThreadRead}
      onMarkThreadUnread={markThreadUnread}
      onArchiveThread={handleArchiveThread}
      threadArchived={threadArchived}
      onMessage={handleMessageSent}
    />
  ) : showListLoading ? (
    // Skeleton replaces the empty state while the first sync is in flight. The
    // empty state otherwise flashes between mount and conversations arriving.
    // A minimum-visible floor also keeps a warm revisit from blinking.
    <InboxSkeleton />
  ) : conversations.length > 0 ? (
    <div className="flex h-full w-full flex-col items-center justify-center">
      <EmptySearch
        icon={() => <MailIcon width={60} height={60} className="text-default" />}
        title="Select a conversation"
        description={`Choose a conversation from the list\nto view it here.`}
      />
    </div>
  ) : (
    <>
      {!data.query && (
        <div className="flex h-full w-full flex-col items-center justify-center">
          <EmptySearch
            icon={() => <MailIcon width={60} height={60} className="text-default" />}
            title={EMPTY_STATE_BY_FILTER[data.filter]?.title ?? `No messages yet`}
            description={
              EMPTY_STATE_BY_FILTER[data.filter]?.description ??
              `You have no conversations for the selected location. If\nyou would like to start a new conversation please click\nthe button below.`
            }
          />
          {!data.filter && (
            <button
              className="mt-8 flex items-center gap-x-2 rounded-md border border-strong px-6 py-3"
              onClick={() =>
                showModal(
                  <div className="w-[800px]">
                    <AddConversationModal
                      conversations={conversations.reduce((acc, c) => ({ ...acc, [c.contactId!]: c.total }), {})}
                      onContactSelect={(contact) => {
                        // No fabricated id when the location (or its tracking
                        // number) hasn't loaded — the reducer keys by contact
                        // until the first send returns the real threadId.
                        const tid = predictSmsThreadId(
                          (location as LocationJson | undefined)?.primaryTrackingNumber,
                          contact.cellPhone,
                        )
                        addConversation(contact, tid)
                      }}
                    />
                  </div>,
                  {
                    title: `Start a new conversation`,
                    isFullScreen: false,
                    removePadding: true,
                    useRadix: true,
                  },
                )
              }
            >
              <PlusIcon width={16} height={16} className="text-default" />
              <p className="text-ui-sm text-default">Start new conversation</p>
            </button>
          )}
        </div>
      )}
      {data.query && (
        // Search now actually matches contact names, so "no results" is a state
        // users reach on a typo rather than a dead end they never saw. Left
        // blank it reads as the search being broken, which is what it was.
        <div className="flex h-full w-full flex-col items-center justify-center">
          <EmptySearch
            icon={() => <MailIcon width={60} height={60} className="text-default" />}
            title="No matching conversations"
            description={`No conversations at this location match\n"${data.query}". Try a name or a phone number.`}
          />
        </div>
      )}
      {isLoading && <div className="hidden" />}
    </>
  )
}
